import React, { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ContextGeneral from "../../servicios/contexto";
import Youtube from "../../componentes/youtube/Youtube";
import { AiOutlineYoutube, AiOutlineInstagram } from "react-icons/ai";
import { FaTwitch, FaArrowRight } from "react-icons/fa";
import useAnimationSettings from "../../hooks/useAnimationSettings";

const VIOLETA = "#697B68";
const ROSA = "#E09767";

const SERVICES = [
  {
    title: "Perfil",
    desc: "Soy Full Stack Software Developer & AI Engineer: desarrollo aplicaciones web y mobile e integro inteligencia artificial para resolver necesidades del negocio.",
  },
  {
    title: "Qué ofrezco",
    desc: "Construyo aplicaciones, APIs, automatizaciones e integraciones con foco en rendimiento, escalabilidad y una experiencia de uso clara.",
  },
  {
    title: "Qué soluciono",
    desc: "Transformo tareas manuales, datos dispersos y herramientas desconectadas en sistemas simples, medibles y sostenibles.",
  },
  {
    title: "Cómo lo hago",
    desc: "Diseño la solución, desarrollo frontend y backend, integro datos e IA y acompaño el despliegue y la adopción.",
  },
  {
    title: "Fin del perfil",
    desc: "Mi objetivo es que la tecnología no sea solo desarrollo, sino una herramienta real para operar mejor y tomar mejores decisiones.",
  },
];

const FEATURES = [
  "Aplicaciones web y mobile, APIs y bases de datos",
  "Inteligencia artificial, automatización e integraciones",
];

const SOCIAL_LINKS = [
  {
    icon: AiOutlineYoutube,
    href: "https://www.youtube.com/channel/UCjnn1S2FKvvkicwEuoBlmAQ",
    label: "YouTube",
  },
  {
    icon: FaTwitch,
    href: "https://www.twitch.tv/sebassdev",
    label: "Twitch",
  },
  {
    icon: AiOutlineInstagram,
    href: "https://instagram.com/",
    label: "Instagram",
  },
];

export default function Home() {
  const navigate = useNavigate();
  const ctx = useContext(ContextGeneral);
  const { setEstadoUsuario, llamadaDB, open, setOpen } = ctx;
  const animation = useAnimationSettings();

  useEffect(() => {
    llamadaDB();
    setEstadoUsuario(0);
    window.scrollTo(0, 0);
    document.title = "Sebas Sotelo | Full Stack Software Developer & AI Engineer";
  }, []); // eslint-disable-line

  const backgroundStyle = {
    backgroundColor: "#EADFD2",
    backgroundImage:
      "linear-gradient(180deg, rgba(248,242,234,0.95) 0%, rgba(234,223,210,0.92) 56%, rgba(248,242,234,1) 100%)",
  };

  return (
    <div className="min-h-screen" style={backgroundStyle}>
      <HeroSection
        navigate={navigate}
        open={open}
        setOpen={setOpen}
        animation={animation}
      />
      <ServicesSection navigate={navigate} animation={animation} />
      <CTASection navigate={navigate} setOpen={setOpen} animation={animation} />
      <TyrSection navigate={navigate} animation={animation} />
      <VideosSection />
    </div>
  );
}

function HeroSection({ navigate, open, setOpen, animation }) {
  return (
    <header className="page-hero relative overflow-hidden lg:flex lg:min-h-screen lg:items-center">
      <BackgroundTexture />

      <div className="page-container relative">
        <div className="grid items-center gap-12 lg:grid-cols-[1.2fr,0.8fr] lg:gap-16">
          <HeroContent
            navigate={navigate}
            open={open}
            setOpen={setOpen}
            animation={animation}
          />
          <HeroImage animation={animation} />
        </div>
      </div>
    </header>
  );
}

function BackgroundTexture() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.06] sm:opacity-[0.08]"
        style={{
          backgroundImage:
            "linear-gradient(90deg, #697B68 1px, transparent 1px), linear-gradient(#697B68 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />
      <div
        className="absolute inset-x-0 top-0 h-28 sm:h-32"
        style={{
          background:
            "linear-gradient(180deg, rgba(105,123,104,0.14), transparent)",
        }}
      />
    </div>
  );
}

function HeroContent({ navigate, open, setOpen, animation }) {
  const { reduceMotion, durations, offsets } = animation;

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: offsets.revealY }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: reduceMotion ? 0 : durations.reveal,
        ease: "easeOut",
      }}
      className="motion-gpu flex min-w-0 flex-col justify-center space-y-8 text-center lg:text-left"
    >
      <HeroHeader animation={animation} />
      <HeroDescription />
      <FeatureList animation={animation} />
      <CTAButtons navigate={navigate} animation={animation} />
      <BotLink open={open} setOpen={setOpen} />
      <SocialLinks animation={animation} />
    </motion.div>
  );
}

function HeroHeader({ animation }) {
  const { reduceMotion, compactMotion, durations, offsets } = animation;

  return (
    <div className="space-y-3">
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, x: -offsets.sideX }}
        animate={{ opacity: 1, x: 0 }}
        transition={{
          duration: reduceMotion ? 0 : durations.reveal,
          delay: reduceMotion ? 0 : compactMotion ? 0.06 : 0.1,
        }}
        className="motion-gpu inline-block"
      >
        <span
          className="mx-auto inline-flex text-xs font-semibold tracking-wider uppercase px-3 py-2 rounded-full sm:text-sm sm:px-4 lg:mx-0"
          style={{ backgroundColor: `${ROSA}1A`, color: ROSA }}
        >
          Software web y mobile · Inteligencia artificial
        </span>
      </motion.div>

      <h1 className="space-y-4">
        <span
          className="hero-name block tracking-tight"
          style={{ color: VIOLETA }}
        >
          Sebas Sotelo
        </span>
        <span
          className="hero-role block"
          style={{ color: "#131613" }}
        >
          Full Stack Software Developer & AI Engineer
        </span>
      </h1>
    </div>
  );
}

function HeroDescription() {
  return (
    <>
      <p
        className="body-copy mx-auto max-w-xl sm:hidden"
        style={{ color: `${VIOLETA}CC` }}
      >
        Desarrollo aplicaciones web y mobile, APIs y soluciones con
        inteligencia artificial para resolver necesidades del negocio.
      </p>
      <p
        className="body-copy mx-auto hidden max-w-xl sm:block lg:mx-0"
        style={{ color: `${VIOLETA}CC` }}
      >
        Desarrollo <strong>aplicaciones web y mobile</strong>, APIs y soluciones
        con <strong>inteligencia artificial</strong>. Combino frontend, backend
        e integraciones con foco en rendimiento, escalabilidad y necesidades
        del negocio.
      </p>
    </>
  );
}

function FeatureList({ animation }) {
  const { reduceMotion, compactMotion, durations, offsets } = animation;

  return (
    <div className="hidden space-y-3 pt-2 sm:block">
      {FEATURES.map((feature, i) => (
        <motion.div
          key={feature}
          initial={reduceMotion ? false : { opacity: 0, x: -offsets.subtleY }}
          animate={{ opacity: 1, x: 0 }}
          transition={{
            duration: reduceMotion ? 0 : durations.reveal * 0.8,
            delay: reduceMotion
              ? 0
              : (compactMotion ? 0.16 : 0.3) + i * durations.stagger,
          }}
          className="motion-gpu flex items-center gap-3 group"
        >
          <div
            className={`w-1.5 h-1.5 rounded-full transition-transform ${
              animation.allowHoverAnimations ? "group-hover:scale-150" : ""
            }`}
            style={{ backgroundColor: ROSA }}
          />
          <span
            className="text-sm sm:text-base"
            style={{ color: `${VIOLETA}BF` }}
          >
            {feature}
          </span>
        </motion.div>
      ))}
    </div>
  );
}

function CTAButtons({ navigate, animation }) {
  const { allowHoverAnimations } = animation;

  return (
    <div className="flex w-full flex-col items-stretch gap-3 pt-4 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:gap-4 sm:pt-4">
      <button
        onClick={() => navigate("/sobre-mi#portfolio")}
        className={`group motion-gpu relative w-full px-6 py-3.5 rounded-2xl sm:w-auto sm:px-8 sm:py-4 font-semibold text-white shadow-lg transition-all duration-300 overflow-hidden ${
          allowHoverAnimations ? "hover:shadow-xl" : ""
        }`}
        style={{ backgroundColor: VIOLETA }}
      >
        <span className="relative z-10">Ver casos con impacto</span>
        <div
          className={`absolute inset-0 opacity-0 transition-opacity duration-300 ${
            allowHoverAnimations ? "group-hover:opacity-100" : ""
          }`}
          style={{
            background: `linear-gradient(135deg, ${VIOLETA} 0%, ${ROSA} 100%)`,
          }}
        />
      </button>

      <button
        onClick={() =>
          window.open(
            "https://drive.google.com/file/d/1jgHbQDQlJLToev5Yn06qrox_4_TeR6n2/view?usp=sharing",
            "_blank",
            "noopener,noreferrer",
          )
        }
        className={`motion-gpu w-full px-6 py-3.5 rounded-2xl font-semibold border-2 sm:w-auto sm:px-8 sm:py-4 transition-all duration-300 ${
          allowHoverAnimations ? "hover:shadow-lg" : ""
        }`}
        style={{
          borderColor: VIOLETA,
          color: VIOLETA,
          backgroundColor: "rgba(255,255,255,0.9)",
        }}
      >
        Ver CV
      </button>
    </div>
  );
}

function BotLink({ open, setOpen }) {
  return (
    <p className="hidden text-sm sm:block" style={{ color: `${VIOLETA}80` }}>
      ¿Tenés dudas?{" "}
      <button
        onClick={() => setOpen(!open)}
        className="font-medium hover:underline underline-offset-4 transition-colors"
        style={{ color: ROSA }}
      >
        Hablá con mi bot -&gt;
      </button>
    </p>
  );
}

function SocialLinks({ animation }) {
  const { allowHoverAnimations } = animation;

  return (
    <div className="flex items-center justify-center gap-3 pt-2 lg:justify-start">
      {SOCIAL_LINKS.map(({ icon: Icon, href, label }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noreferrer"
          className={`motion-gpu w-11 h-11 rounded-xl flex items-center justify-center border-2 transition-all duration-300 ${
            allowHoverAnimations ? "hover:scale-110 hover:shadow-md" : ""
          }`}
          style={{
            borderColor: `${VIOLETA}20`,
            color: VIOLETA,
            backgroundColor: "rgba(255,255,255,0.9)",
          }}
          aria-label={label}
        >
          <Icon className="text-xl" />
        </a>
      ))}
    </div>
  );
}

function HeroImage({ animation }) {
  const { reduceMotion, compactMotion, durations } = animation;

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration: reduceMotion ? 0 : durations.hero,
        delay: reduceMotion ? 0 : compactMotion ? 0.12 : 0.2,
        ease: "easeOut",
      }}
      className="motion-gpu relative mx-auto w-full max-w-[15rem] sm:max-w-[22rem]"
    >
      <div className="relative w-full aspect-square">
        <img
          src="/logo.png"
          alt="Sebas Sotelo"
          className="relative z-10 h-full w-full rounded-full object-contain drop-shadow-[0_18px_30px_rgba(105,123,104,0.24)]"
        />
      </div>
    </motion.div>
  );
}

function ServicesSection({ navigate, animation }) {
  return (
    <section className="portfolio-section relative">
      <div className="mx-auto max-w-7xl">
        <SectionHeader title="Perfil y propuesta" animation={animation} />

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-6">
          {SERVICES.map((service, i) => (
            <ServiceCard
              key={service.title}
              service={service}
              index={i}
              navigate={navigate}
              animation={animation}
              layoutClassName={
                i === 3 ? "lg:col-start-2" : i === 4 ? "lg:col-start-4" : ""
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionHeader({ title, animation }) {
  const { reduceMotion, durations, offsets, viewport } = animation;

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: offsets.revealY }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={viewport}
      transition={{ duration: reduceMotion ? 0 : durations.reveal }}
      className="motion-gpu text-center mb-10 md:mb-16"
    >
      <h2
        className="section-title mb-4"
        style={{ color: VIOLETA }}
      >
        {title}
      </h2>
      <div
        className="w-20 h-1 mx-auto rounded-full"
        style={{
          background: `linear-gradient(135deg, ${ROSA} 0%, ${VIOLETA} 100%)`,
        }}
      />
    </motion.div>
  );
}

function ServiceCard({
  service,
  index,
  navigate,
  animation,
  layoutClassName = "",
}) {
  const { reduceMotion, durations, offsets, viewport, allowHoverAnimations } =
    animation;

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: offsets.revealY }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={viewport}
      transition={{
        duration: reduceMotion ? 0 : durations.reveal * 0.6,
        delay: reduceMotion ? 0 : index * durations.stagger,
      }}
      className={`group motion-gpu relative overflow-hidden card-padding rounded-2xl shadow-md md:rounded-3xl md:shadow-lg transition-all duration-500 lg:col-span-2 ${layoutClassName} ${
        allowHoverAnimations ? "hover:shadow-2xl" : ""
      }`}
      style={{
        backgroundColor: "rgba(255,255,255,0.9)",
        border: `1px solid ${VIOLETA}1A`,
      }}
    >
      <div className="space-y-4 relative z-10">
        <h3
          className="card-title"
          style={{ color: VIOLETA }}
        >
          {service.title}
        </h3>
        <p
          className="body-copy"
          style={{ color: `${VIOLETA}99` }}
        >
          {service.desc}
        </p>
        <button
          onClick={() => navigate("/sobre-mi#portfolio")}
          className={`inline-flex items-center gap-2 text-sm font-semibold transition-all ${
            allowHoverAnimations ? "group-hover:gap-3" : ""
          }`}
          style={{ color: ROSA }}
        >
          Ver casos <FaArrowRight className="text-xs" />
        </button>
      </div>

      <div
        className={`absolute bottom-0 left-0 right-0 h-[4px] w-0 transition-all duration-500 ${
          allowHoverAnimations ? "group-hover:w-full" : ""
        }`}
        style={{
          background: `linear-gradient(135deg, ${ROSA} 0%, ${VIOLETA} 100%)`,
          borderBottomLeftRadius: "1.5rem",
          borderBottomRightRadius: "1.5rem",
        }}
      />
    </motion.div>
  );
}

function CTASection({ navigate, setOpen, animation }) {
  const { reduceMotion, durations, offsets, viewport, allowHoverAnimations } =
    animation;

  return (
    <section className="portfolio-section relative">
      <div className="mx-auto max-w-5xl">
        <motion.div
          initial={
            reduceMotion ? false : { opacity: 0, y: offsets.revealY + 4 }
          }
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewport}
          transition={{
            duration: reduceMotion ? 0 : durations.reveal,
            ease: "easeOut",
          }}
          className="motion-gpu relative overflow-hidden rounded-[2.25rem] p-[1px] shadow-2xl"
          style={{
            background: `linear-gradient(135deg, ${ROSA}, ${VIOLETA})`,
            boxShadow:
              "0 12px 40px rgba(105,123,104,0.18), 0 4px 12px rgba(105,123,104,0.10)",
          }}
        >
          <div
            className="relative rounded-[1.4rem] bg-white/90 p-5 backdrop-blur sm:rounded-[2.1rem] sm:p-12"
            style={{ border: `1px solid ${VIOLETA}1A` }}
          >
            <div className="pointer-events-none absolute inset-0">
              <div
                className="absolute -top-8 -right-8 h-56 w-56 rounded-full blur-3xl opacity-25"
                style={{
                  background: `linear-gradient(135deg, rgba(224,151,103,0.18), transparent)`,
                }}
              />
              <div
                className="absolute -bottom-10 -left-10 h-64 w-64 rounded-full blur-3xl opacity-20"
                style={{
                  background: `linear-gradient(135deg, rgba(105,123,104,0.16), transparent)`,
                }}
              />
            </div>

            <div className="relative grid gap-8 lg:grid-cols-[1.2fr,0.8fr] lg:items-center">
              <div className="space-y-5">
                <div
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2"
                  style={{ backgroundColor: `${ROSA}14`, color: ROSA }}
                >
                  <span className="text-sm font-black leading-none">+</span>
                  <span className="whitespace-nowrap text-xs font-semibold tracking-wider uppercase">
                    Fin del perfil
                  </span>
                </div>

                <h3
                  className="section-title"
                  style={{ color: VIOLETA }}
                >
                  Técnica, criterio y adopción en una misma dirección
                </h3>

                <p
                  className="body-copy"
                  style={{ color: `${VIOLETA}BF` }}
                >
                  Mi foco no es solo construir software: es entender el
                  problema, elegir la solución correcta y acompañar la
                  implementación hasta que genere valor real.
                </p>

                <ul className="readable-grid mt-2 hidden sm:grid">
                  {[
                    "Entender operación y problema",
                    "Elegir qué desarrollar o integrar",
                    "Liderar implementación y adopción",
                    "Medir impacto y mejorar",
                  ].map((b) => (
                    <li
                      key={b}
                      className="flex items-start justify-start gap-2 text-left"
                    >
                      <span
                        className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full"
                        style={{ backgroundColor: ROSA }}
                      />
                      <span
                        className="small-copy"
                        style={{ color: `${VIOLETA}CC` }}
                      >
                        {b}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="flex flex-wrap items-center gap-4 pt-2">
                  <button
                    onClick={() => navigate("/sobre-mi")}
                    className={`group motion-gpu relative inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 font-semibold text-white shadow-lg transition-all ${
                      allowHoverAnimations ? "hover:shadow-xl" : ""
                    }`}
                    style={{ backgroundColor: VIOLETA }}
                  >
                    Ver perfil
                    <motion.span
                      initial={{ x: 0 }}
                      whileHover={allowHoverAnimations ? { x: 4 } : undefined}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 28,
                      }}
                      className="text-sm"
                    >
                      -&gt;
                    </motion.span>
                    <span
                      className={`pointer-events-none absolute inset-0 rounded-xl opacity-0 transition-opacity duration-300 ${
                        allowHoverAnimations ? "group-hover:opacity-100" : ""
                      }`}
                      style={{
                        background: `linear-gradient(135deg, ${VIOLETA}, ${ROSA})`,
                        mixBlendMode: "overlay",
                      }}
                    />
                  </button>

                  <button
                    onClick={() =>
                      window.open(
                        "https://www.linkedin.com/in/sebassotelo/",
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                    className={`motion-gpu inline-flex items-center justify-center rounded-xl px-6 py-3 font-semibold transition-all ${
                      allowHoverAnimations ? "hover:shadow" : ""
                    }`}
                    style={{
                      border: `2px solid ${VIOLETA}`,
                      color: VIOLETA,
                      backgroundColor: "white",
                    }}
                  >
                    LinkedIn
                  </button>
                </div>

                <p className="text-xs mt-2" style={{ color: `${VIOLETA}80` }}>
                  ¿Tenés dudas rápidas?{" "}
                  <button
                    onClick={() => setOpen(true)}
                    className="underline underline-offset-4 hover:opacity-90"
                    style={{ color: ROSA }}
                  >
                    hablá con mi bot
                  </button>
                  .
                </p>
              </div>

              <div className="relative">
                <div
                  className="mx-auto w-40 h-40 sm:w-48 sm:h-48 rounded-3xl grid place-items-center shadow-2xl"
                  style={{
                    backgroundColor: "white",
                    border: `1px solid ${VIOLETA}1A`,
                  }}
                >
                  <div className="text-center">
                    <div className="text-4xl mb-2" style={{ color: ROSA }}>
                      FS + AI
                    </div>
                    <div
                      className="text-sm font-semibold"
                      style={{ color: VIOLETA }}
                    >
                      Software Development
                    </div>
                    <div className="text-xs" style={{ color: `${VIOLETA}99` }}>
                      Web + Mobile + IA
                    </div>
                  </div>
                </div>
                <div
                  className="absolute -inset-4 -z-10 rounded-[2rem] blur-2xl opacity-40"
                  style={{
                    background: `linear-gradient(135deg, ${ROSA}, ${VIOLETA})`,
                  }}
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function TyrSection({ navigate, animation }) {
  const { reduceMotion, durations, offsets, viewport, allowHoverAnimations } =
    animation;

  const points = [
    "Empresa creada para sistematizar una metodología de trabajo",
    "Software, automatización e IA orientadas a uso real",
    "Complejidad técnica absorbida para simplificar la operación",
  ];

  return (
    <section className="relative px-[var(--page-gutter)] pb-[var(--section-space)]">
      <div className="mx-auto max-w-5xl">
        <motion.div
          initial={
            reduceMotion ? false : { opacity: 0, y: offsets.revealY + 2 }
          }
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewport}
          transition={{
            duration: reduceMotion ? 0 : durations.reveal,
            ease: "easeOut",
          }}
          className="motion-gpu relative overflow-hidden rounded-[2.25rem] p-[1px] shadow-2xl"
          style={{
            background: `linear-gradient(135deg, ${ROSA}, ${VIOLETA})`,
            boxShadow:
              "0 12px 40px rgba(105,123,104,0.14), 0 4px 12px rgba(105,123,104,0.08)",
          }}
        >
          <div
            className="relative rounded-[2.1rem] bg-white/90 backdrop-blur p-6 sm:p-8"
            style={{ border: `1px solid ${VIOLETA}1A` }}
          >
            <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[2.1rem]">
              <div
                className="absolute -top-10 right-0 h-40 w-40 rounded-full blur-3xl opacity-20"
                style={{
                  background: `linear-gradient(135deg, rgba(224,151,103,0.14), transparent)`,
                }}
              />
              <div
                className="absolute bottom-0 -left-8 h-44 w-44 rounded-full blur-3xl opacity-15"
                style={{
                  background: `linear-gradient(135deg, rgba(105,123,104,0.12), transparent)`,
                }}
              />
            </div>

            <div className="relative grid gap-5 lg:grid-cols-[0.9fr,1.1fr] lg:items-center">
              <div
                className="relative hidden overflow-hidden rounded-[1.75rem] p-6 sm:block sm:p-7"
                style={{
                  background: `linear-gradient(145deg, rgba(255,255,255,0.96) 0%, rgba(246,240,249,0.96) 100%)`,
                  border: `1px solid ${VIOLETA}12`,
                }}
              >
                <div className="space-y-6">
                  <div className="flex items-center justify-between gap-4">
                    <div
                      className="rounded-[1.35rem] bg-white px-5 py-4 shadow-[0_12px_30px_rgba(105,123,104,0.10)]"
                      style={{ border: `1px solid ${VIOLETA}14` }}
                    >
                      <img
                        src="/TYR logo negro.png"
                        alt="TYR"
                        className="h-16 w-auto object-contain sm:h-[4.5rem]"
                      />
                    </div>
                    <span
                      className="rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em]"
                      style={{ backgroundColor: `${ROSA}14`, color: ROSA }}
                    >
                      tyr-ai.com.ar
                    </span>
                  </div>

                  <div className="readable-grid">
                    {[
                      ["Metodo", "Sistematizado y replicable"],
                      ["Enfoque", "Tecnología útil para equipos"],
                    ].map(([label, value]) => (
                      <div
                        key={label}
                        className="rounded-2xl px-4 py-3"
                        style={{
                          backgroundColor: "rgba(255,255,255,0.72)",
                          border: `1px solid ${VIOLETA}12`,
                        }}
                      >
                        <p
                          className="text-xs font-semibold uppercase tracking-[0.18em]"
                          style={{ color: ROSA }}
                        >
                          {label}
                        </p>
                        <p
                          className="small-copy mt-1 font-semibold"
                          style={{ color: VIOLETA }}
                        >
                          {value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-4 text-center lg:text-left">
                <span
                  className="inline-flex rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider"
                  style={{ backgroundColor: `${ROSA}14`, color: ROSA }}
                >
                  TYR
                </span>

                <div className="space-y-3">
                  <h3
                    className="card-title"
                    style={{ color: VIOLETA }}
                  >
                    La empresa que sistematiza mi forma de resolver tecnología
                  </h3>
                  <p
                    className="body-copy"
                    style={{ color: `${VIOLETA}BF` }}
                  >
                    TYR transforma una metodología de trabajo en una estructura
                    clara para implementar software, automatización e IA de
                    forma simple para quienes la usan todos los días.
                  </p>
                </div>

                <div className="hidden gap-3 sm:grid">
                  {points.map((point) => (
                    <div
                      key={point}
                      className="flex items-start gap-3 text-left"
                    >
                      <span
                        className="mt-1.5 h-2.5 w-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: ROSA }}
                      />
                      <p
                        className="small-copy"
                        style={{ color: `${VIOLETA}CC` }}
                      >
                        {point}
                      </p>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => navigate("/tyr")}
                  className={`group motion-gpu inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold text-white shadow-lg transition-all ${
                    allowHoverAnimations ? "hover:shadow-xl hover:gap-3" : ""
                  }`}
                  style={{ backgroundColor: VIOLETA }}
                >
                  Conocer TYR <FaArrowRight className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function VideosSection() {
  return (
    <section className="portfolio-section">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex flex-col items-start gap-3 text-left sm:flex-row sm:items-center sm:justify-between sm:text-center md:mb-10">
          <h3
            className="section-title"
            style={{ color: VIOLETA }}
          >
            Contenido y aprendizajes
          </h3>
          <a
            href="https://www.youtube.com/@SebassDev"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold hover:gap-3 transition-all"
            style={{ color: ROSA }}
          >
            Ver canal <FaArrowRight className="text-xs" />
          </a>
        </div>
        <Youtube />
      </div>
    </section>
  );
}
